import React, { useState, useEffect } from 'react';
import { S3Client, ListObjectsV2Command, PutObjectCommand, DeleteObjectCommand, GetObjectCommand } from '@aws-sdk/client-s3';
import { getSignedUrl } from '@aws-sdk/s3-request-presigner';
import { fromCognitoIdentityPool } from '@aws-sdk/credential-providers';
import { Upload, Download, Trash2, RefreshCw, AlertCircle, CheckCircle, Folder, File, ChevronRight, Home, FolderPlus, LogOut } from 'lucide-react';
import Auth from './Auth';
import './App.css';

const AppWithFolders = () => {
  const [files, setFiles] = useState([]);
  const [currentPath, setCurrentPath] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [s3Client, setS3Client] = useState(null);
  const [session, setSession] = useState(null);
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');

  const handleAuthSuccess = (cognitoSession) => {
    setSession(cognitoSession);
    initializeS3Client(cognitoSession);
  };

  const handleLogout = () => {
    setSession(null);
    setS3Client(null);
    setFiles([]);
    setCurrentPath('');
  };

  const initializeS3Client = async (cognitoSession) => {
    const region = process.env.REACT_APP_AWS_REGION || 'us-east-1';
    const userPoolId = process.env.REACT_APP_COGNITO_USER_POOL_ID;
    const identityPoolId = process.env.REACT_APP_COGNITO_IDENTITY_POOL_ID;

    if (!userPoolId || !identityPoolId) {
      setError('Configuração do Cognito não encontrada');
      return;
    }

    try {
      const idToken = cognitoSession.getIdToken().getJwtToken();

      const credentials = fromCognitoIdentityPool({
        identityPoolId,
        logins: {
          [`cognito-idp.${region}.amazonaws.com/${userPoolId}`]: idToken,
        },
        clientConfig: { region },
      });

      const client = new S3Client({
        region,
        credentials,
      });

      setS3Client(client);
    } catch (err) {
      setError(`Erro ao inicializar S3: ${err.message}`);
      console.error(err);
    }
  };

  useEffect(() => {
    if (s3Client) {
      loadFiles();
    }
  }, [s3Client, currentPath]);

  const loadFiles = async () => {
    if (!s3Client) return;

    setLoading(true);
    setError(null);

    try {
      const bucketName = process.env.REACT_APP_S3_BUCKET_NAME;
      if (!bucketName) {
        throw new Error('Nome do bucket S3 não configurado');
      }

      const command = new ListObjectsV2Command({
        Bucket: bucketName,
        Prefix: currentPath,
        Delimiter: '/',
      });

      const response = await s3Client.send(command);

      const folders = (response.CommonPrefixes || []).map((prefix) => ({
        key: prefix.Prefix,
        name: prefix.Prefix.replace(currentPath, '').replace('/', ''),
        isFolder: true,
      }));

      const fileList = (response.Contents || [])
        .filter((item) => item.Key !== currentPath)
        .map((item) => ({
          key: item.Key,
          name: item.Key.replace(currentPath, ''),
          size: item.Size,
          lastModified: item.LastModified,
          isFolder: false,
        }));

      setFiles([...folders, ...fileList]);
    } catch (err) {
      setError(`Erro ao carregar arquivos: ${err.message}`);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setError(null);
    setSuccess(null);

    try {
      const bucketName = process.env.REACT_APP_S3_BUCKET_NAME;
      if (!bucketName) {
        throw new Error('Nome do bucket S3 não configurado');
      }

      const arrayBuffer = await file.arrayBuffer();
      const key = currentPath + file.name;

      const command = new PutObjectCommand({
        Bucket: bucketName,
        Key: key,
        Body: new Uint8Array(arrayBuffer),
        ContentType: file.type,
      });

      await s3Client.send(command);
      setSuccess(`Arquivo "${file.name}" enviado com sucesso!`);
      await loadFiles();
    } catch (err) {
      setError(`Erro ao enviar arquivo: ${err.message}`);
      console.error(err);
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const handleDownload = async (fileKey) => {
    try {
      const bucketName = process.env.REACT_APP_S3_BUCKET_NAME;
      if (!bucketName) {
        throw new Error('Nome do bucket S3 não configurado');
      }

      const fileName = fileKey.split('/').pop();

      const command = new GetObjectCommand({
        Bucket: bucketName,
        Key: fileKey,
        ResponseContentDisposition: `attachment; filename="${fileName}"`,
      });

      const signedUrl = await getSignedUrl(s3Client, command, { expiresIn: 3600 });

      const link = document.createElement('a');
      link.href = signedUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      setError(`Erro ao gerar URL de download: ${err.message}`);
      console.error(err);
    }
  };

  const handleDelete = async (fileKey, isFolder) => {
    const itemType = isFolder ? 'pasta' : 'arquivo';
    if (!window.confirm(`Tem certeza que deseja deletar ${itemType} "${fileKey}"?`)) {
      return;
    }

    setError(null);
    setSuccess(null);

    try {
      const bucketName = process.env.REACT_APP_S3_BUCKET_NAME;
      if (!bucketName) {
        throw new Error('Nome do bucket S3 não configurado');
      }

      const command = new DeleteObjectCommand({
        Bucket: bucketName,
        Key: fileKey,
      });

      await s3Client.send(command);
      setSuccess(`${itemType.charAt(0).toUpperCase() + itemType.slice(1)} deletado com sucesso!`);
      await loadFiles();
    } catch (err) {
      setError(`Erro ao deletar ${itemType}: ${err.message}`);
      console.error(err);
    }
  };

  const handleFolderClick = (folderKey) => {
    setCurrentPath(folderKey);
  };

  const handleBreadcrumbClick = (path) => {
    setCurrentPath(path);
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      setError('Digite um nome para a pasta');
      return;
    }

    setError(null);
    setSuccess(null);

    try {
      const bucketName = process.env.REACT_APP_S3_BUCKET_NAME;
      if (!bucketName) {
        throw new Error('Nome do bucket S3 não configurado');
      }

      const folderKey = currentPath + newFolderName.trim() + '/';

      const command = new PutObjectCommand({
        Bucket: bucketName,
        Key: folderKey,
        Body: '',
      });

      await s3Client.send(command);
      setSuccess(`Pasta "${newFolderName}" criada com sucesso!`);
      setNewFolderName('');
      setShowNewFolder(false);
      await loadFiles();
    } catch (err) {
      setError(`Erro ao criar pasta: ${err.message}`);
      console.error(err);
    }
  };

  const getBreadcrumbs = () => {
    if (!currentPath) return [];

    const parts = currentPath.split('/').filter(Boolean);
    const breadcrumbs = [];
    let path = '';

    parts.forEach((part, index) => {
      path += part + '/';
      breadcrumbs.push({ name: part, path });
    });

    return breadcrumbs;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('pt-BR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (!session) {
    return <Auth onAuthSuccess={handleAuthSuccess} />;
  }

  return (
    <div className="app-container">
      <div className="app-content">
        <header className="app-header">
          <div>
            <h1>Market Cloud S3</h1>
            <p>Gerenciamento de Arquivos</p>
          </div>
          <button onClick={handleLogout} className="logout-button" title="Sair">
            <LogOut size={20} />
            <span>Sair</span>
          </button>
        </header>

        {error && (
          <div className="alert alert-error">
            <AlertCircle size={20} />
            <span>{error}</span>
          </div>
        )}

        {success && (
          <div className="alert alert-success">
            <CheckCircle size={20} />
            <span>{success}</span>
          </div>
        )}

        <div className="breadcrumb">
          <button onClick={() => handleBreadcrumbClick('')} className="breadcrumb-item">
            <Home size={16} />
            <span>Raiz</span>
          </button>
          {getBreadcrumbs().map((crumb, index) => (
            <React.Fragment key={index}>
              <ChevronRight size={16} className="breadcrumb-separator" />
              <button onClick={() => handleBreadcrumbClick(crumb.path)} className="breadcrumb-item">
                {crumb.name}
              </button>
            </React.Fragment>
          ))}
        </div>

        <div className="actions-bar">
          <label className="upload-label-inline">
            <input
              type="file"
              onChange={handleFileUpload}
              disabled={uploading || !s3Client}
              className="upload-input"
            />
            <div className="action-button-inline upload-btn">
              <Upload size={18} />
              <span>{uploading ? 'Enviando...' : 'Upload'}</span>
            </div>
          </label>

          <button onClick={() => setShowNewFolder(!showNewFolder)} className="action-button-inline folder-btn">
            <FolderPlus size={18} />
            <span>Nova Pasta</span>
          </button>

          <button onClick={loadFiles} disabled={loading} className="action-button-inline refresh-btn">
            <RefreshCw size={18} className={loading ? 'spinning' : ''} />
            <span>Atualizar</span>
          </button>
        </div>

        {showNewFolder && (
          <div className="new-folder-form">
            <input
              type="text"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              placeholder="Nome da pasta"
              onKeyPress={(e) => e.key === 'Enter' && handleCreateFolder()}
            />
            <button onClick={handleCreateFolder} className="create-folder-btn">Criar</button>
            <button onClick={() => { setShowNewFolder(false); setNewFolderName(''); }} className="cancel-folder-btn">Cancelar</button>
          </div>
        )}

        <div className="files-section">
          <div className="files-header">
            <h2>Arquivos ({files.length})</h2>
          </div>

          {loading ? (
            <div className="loading">Carregando arquivos...</div>
          ) : files.length === 0 ? (
            <div className="empty-state">
              <p>Nenhum arquivo encontrado</p>
              <p className="empty-state-hint">Envie um arquivo ou crie uma pasta para começar</p>
            </div>
          ) : (
            <div className="files-list">
              {files.map((file) => (
                <div key={file.key} className="file-item">
                  <div className="file-info" onClick={() => file.isFolder && handleFolderClick(file.key)} style={{ cursor: file.isFolder ? 'pointer' : 'default' }}>
                    <div className="file-icon">
                      {file.isFolder ? <Folder size={24} color="#667eea" /> : <File size={24} color="#999" />}
                    </div>
                    <div className="file-details">
                      <div className="file-name">{file.name}</div>
                      {!file.isFolder && (
                        <div className="file-meta">
                          <span className="file-size">{formatFileSize(file.size)}</span>
                          <span className="file-date">{formatDate(file.lastModified)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="file-actions">
                    {!file.isFolder && (
                      <button
                        onClick={() => handleDownload(file.key)}
                        className="action-button download-button"
                        title="Download"
                      >
                        <Download size={18} />
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(file.key, file.isFolder)}
                      className="action-button delete-button"
                      title="Deletar"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AppWithFolders;
